import { HASHED_CLASS_PATTERNS } from './constants';

/** Determina si una clase parece generada dinámicamente (hash de CSS-in-JS, etc.). */
export function isHashedClass(className: string): boolean {
  return HASHED_CLASS_PATTERNS.some((pattern) => pattern.test(className));
}

/** Filtra las clases de un elemento para quedarse solo con las "humanas" (estables). */
export function filterHumanClasses(element: Element): string[] {
  return Array.from(element.classList).filter((c) => !isHashedClass(c) && c.length < 64);
}

/**
 * Atributos estables y semánticos que identifican un elemento mejor que sus
 * clases utilitarias (que cambian con el styling). Orden = prioridad. Producen
 * selectores legibles tipo `button[aria-label="Abrir menú"]` que el usuario
 * puede escribir/reconocer en el editor de segmentos.
 */
const STABLE_ATTRIBUTES = [
  'data-testid',
  'data-test',
  'data-cy',
  'name',
  'aria-label',
  'placeholder',
  'alt',
  'title',
  // `href` identifica muy bien a los links (`<a>`), un target frecuente de
  // autocapture; va después de los atributos semánticos pero antes de los
  // genéricos. Valores largos (URLs absolutas con query) se descartan por
  // MAX_ATTR_VALUE_LENGTH.
  'href',
  'role',
  'type',
] as const;

/** Valores de atributo más largos que esto se descartan (no son buenos selectores). */
const MAX_ATTR_VALUE_LENGTH = 100;

/** Escape básico de caracteres especiales en CSS (usa CSS.escape si está disponible). */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }
  return value.replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, '\\$&');
}

/**
 * Escape para un valor que va DENTRO de comillas en `[attr="..."]`. A diferencia
 * de `cssEscape`, no escapa espacios ni acentos (las comillas ya los cubren):
 * solo `\` y `"`. Así el valor queda igual al texto real y el match por substring
 * del backend funciona con lo que el usuario escribe.
 */
function escapeAttrValue(value: string): string {
  return value.replace(/[\\"]/g, '\\$&');
}

/** Devuelve un selector por atributo estable si el elemento tiene alguno. */
function stableAttributeSelector(element: Element, tag: string): string | null {
  for (const attr of STABLE_ATTRIBUTES) {
    const value = element.getAttribute(attr);
    if (value && value.length > 0 && value.length <= MAX_ATTR_VALUE_LENGTH) {
      return `${tag}[${attr}="${escapeAttrValue(value)}"]`;
    }
  }
  return null;
}

/** Construye un selector simple y estable para UN elemento. */
function buildElementSelector(element: Element): string {
  const tag = element.tagName.toLowerCase();

  // 1. `data-veo-tag`: el nombre semántico que da el cliente. Máxima prioridad.
  const veoTag = element.getAttribute('data-veo-tag');
  if (veoTag) {
    return `${tag}[data-veo-tag="${escapeAttrValue(veoTag)}"]`;
  }

  // 2. `id`: único y estable. Lo combinamos con clases humanas (como antes).
  if (element.id) {
    const classes = filterHumanClasses(element);
    const classPart = classes.length > 0 ? `.${classes.map(cssEscape).join('.')}` : '';
    return `${tag}#${cssEscape(element.id)}${classPart}`;
  }

  // 3. Atributo estable (aria-label, name, type, role…): más robusto que clases.
  const attrSelector = stableAttributeSelector(element, tag);
  if (attrSelector) return attrSelector;

  // 4. Fallback: clases humanas.
  const classes = filterHumanClasses(element);
  const classPart = classes.length > 0 ? `.${classes.map(cssEscape).join('.')}` : '';
  return `${tag}${classPart}`;
}

/** Construye un selector CSS legible y razonablemente estable para el elemento. */
export function buildSelectorPath(element: Element, maxAncestors = 5): string {
  const parts: string[] = [];
  let current: Element | null = element;
  let depth = 0;

  while (current && current !== document.documentElement && depth <= maxAncestors) {
    parts.unshift(buildElementSelector(current));
    current = current.parentElement;
    depth++;
  }

  return parts.join(' > ');
}
